import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

type QueryState<T extends Record<string, any>> = {
    state: T;
    setQueryState: (query: Partial<T>) => void;
};

export function useQueryState<T extends Record<string, any>>(
    initState: T
): QueryState<T> {
    const location = useLocation();
    const navigate = useNavigate();

    const [state, setState] = useState<T>(() => {
        const searchParams = new URLSearchParams(location.search);
        if (!searchParams.toString()) {
            return initState;
        }

        const queryObject = Array.from(searchParams.entries()).reduce(
            (acc, [key, value]) => {
                acc[key] = value;
                return acc;
            },
            {} as any
        );

        return queryObject;
    });

    const setQueryState: QueryState<T>['setQueryState'] = (query) => {
        const merged = { ...state, ...query };

        // Convert tất cả value sang string để đưa lên URL
        const queryString = new URLSearchParams(
            Object.entries(merged).reduce<Record<string, string>>(
                (acc, [key, value]) => {
                    acc[key] = String(value);
                    return acc;
                },
                {}
            )
        ).toString();

        const newPathName = `${location.pathname}?${queryString}`;

        navigate(newPathName, { replace: true }); // replace tránh push lịch sử thừa

        setState(merged);
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        if (!searchParams.toString()) {
            setState(initState);
            return;
        }

        const queryObject = Array.from(searchParams.entries()).reduce(
            (acc, [key, value]) => {
                acc[key] = value;
                return acc;
            },
            {} as Record<string, string>
        );

        setState((prev) => ({
            ...prev,
            ...queryObject,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    return { state, setQueryState };
}
